/**
 * ThemePicker — 2×3 grid of 5 preset theme tiles + 1 "Custom" tile.
 *
 * Each preset tile is rendered in *that* theme's colours so the grid itself
 * is a live preview. The 6th tile opens the AI-art theme studio.
 *
 * Onboarding: used when no stored theme and `hyggebam.themePicked` flag is unset.
 * Nav/settings: opened from `nav-theme` or "Open theme studio" button.
 *
 * data-testid map:
 *   theme-picker, theme-preset (+data-theme-id), theme-custom-tile, theme-skip
 */
import { PRESET_THEMES, type Theme } from "@hyggebam/shared";
import { applyTheme, storeTheme } from "../theme.ts";

// ── Palette swatch row ───────────────────────────────────────────────────────
function SwatchRow({ palette }: { palette: Theme["palette"] }) {
  const colors = [
    palette.accent,
    palette.accent2,
    palette.like,
    palette.never,
    palette.later,
  ];
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
      {colors.map((c, i) => (
        <div
          key={i}
          style={{
            width: 14,
            height: 14,
            borderRadius: "50%",
            background: c,
            border: "1px solid rgba(255,255,255,0.25)",
            flexShrink: 0,
          }}
        />
      ))}
    </div>
  );
}

// ── Single preset tile ────────────────────────────────────────────────────────
function PresetTile({
  theme,
  onClick,
}: {
  theme: Theme;
  onClick: () => void;
}) {
  const radiusPx = `${Math.min(theme.radius, 18)}px`;
  return (
    <button
      data-testid="theme-preset"
      data-theme-id={theme.id}
      onClick={onClick}
      style={{
        background: theme.palette.bg,
        borderRadius: radiusPx,
        border: `2px solid ${theme.palette.accent}44`,
        padding: "14px 12px",
        cursor: "pointer",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: 2,
        transition: "transform 0.12s, box-shadow 0.12s",
        boxShadow: `0 4px 18px ${theme.palette.accent}22`,
        minHeight: 110,
      }}
      onMouseDown={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.96)";
      }}
      onMouseUp={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
      }}
      onTouchStart={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.96)";
      }}
      onTouchEnd={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
      }}
    >
      {/* Accent strip at top */}
      <div
        style={{
          height: 4,
          borderRadius: 999,
          background: `linear-gradient(90deg, ${theme.palette.accent}, ${theme.palette.accent2})`,
          marginBottom: 6,
        }}
      />
      {/* Theme name */}
      <div
        style={{
          color: theme.palette.ink,
          fontWeight: 800,
          fontSize: 15,
          letterSpacing: "-0.2px",
        }}
      >
        {theme.name}
      </div>
      {/* Muted subtext using ink colour at reduced opacity */}
      <div
        style={{
          color: theme.palette.muted,
          fontSize: 11,
          lineHeight: 1.3,
        }}
      >
        Tap to preview
      </div>
      <SwatchRow palette={theme.palette} />
    </button>
  );
}

// ── Custom tile ───────────────────────────────────────────────────────────────
function CustomTile({ onClick }: { onClick: () => void }) {
  return (
    <button
      data-testid="theme-custom-tile"
      onClick={onClick}
      style={{
        background: "linear-gradient(135deg, #1c1830 60%, #2d1f45)",
        borderRadius: 18,
        border: "2px dashed rgba(255,255,255,0.18)",
        padding: "14px 12px",
        cursor: "pointer",
        textAlign: "left",
        display: "flex",
        flexDirection: "column",
        gap: 4,
        minHeight: 110,
        transition: "transform 0.12s",
      }}
      onMouseDown={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.96)";
      }}
      onMouseUp={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
      }}
      onTouchStart={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "scale(0.96)";
      }}
      onTouchEnd={(e) => {
        (e.currentTarget as HTMLButtonElement).style.transform = "scale(1)";
      }}
    >
      <div style={{ fontSize: 22, marginBottom: 2 }}>✨</div>
      <div
        style={{
          color: "#f3eefe",
          fontWeight: 800,
          fontSize: 15,
          letterSpacing: "-0.2px",
        }}
      >
        Custom
      </div>
      <div
        style={{
          color: "#a79fc4",
          fontSize: 11,
          lineHeight: 1.3,
        }}
      >
        Type a vibe, get AI art
      </div>
    </button>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export function ThemePicker({
  onPickPreset,
  onCustom,
  onSkip,
  showSkip = false,
}: {
  onPickPreset: (theme: Theme) => void;
  onCustom: () => void;
  onSkip?: () => void;
  showSkip?: boolean;
}) {
  function pickPreset(theme: Theme) {
    applyTheme(theme);
    storeTheme(theme);
    onPickPreset(theme);
  }

  return (
    <div className="scroll" data-testid="theme-picker">
      <h1>Choose your vibe</h1>
      <p className="sub" style={{ marginBottom: 20 }}>
        Pick a look for the app — you can always change it later.
      </p>

      {/* 2-column grid */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 12,
        }}
      >
        {PRESET_THEMES.map((t) => (
          <PresetTile key={t.id} theme={t} onClick={() => pickPreset(t)} />
        ))}
        <CustomTile onClick={onCustom} />
      </div>

      {/* Skip button — shown during onboarding */}
      {showSkip && onSkip && (
        <div style={{ marginTop: 20, textAlign: "center" }}>
          <button
            data-testid="theme-skip"
            className="btn ghost"
            onClick={onSkip}
            style={{ fontSize: 14 }}
          >
            Skip / keep default
          </button>
        </div>
      )}

      <div style={{ height: 24 }} />
    </div>
  );
}
